/**
 * BindingRepository Service - Zustand Store
 * Single Responsibility: Store and retrieve binding configurations
 */

import { create } from 'zustand';
import type { DataBindingConfig } from '../types/index';

export interface BindingRepositoryState {
  bindings: Map<string, DataBindingConfig>;
}

export interface BindingRepositoryActions {
  /**
   * Store a binding configuration
   */
  set: (formID: string, config: DataBindingConfig) => void;

  /**
   * Get binding configuration for a form
   */
  get: (formID: string) => DataBindingConfig | undefined;

  /**
   * Get all binding configurations
   */
  getAll: () => DataBindingConfig[];

  /**
   * Delete a binding configuration
   */
  delete: (formID: string) => void;

  /**
   * Check if binding exists
   */
  has: (formID: string) => boolean;

  /**
   * Clear all bindings
   */
  clear: () => void;
}

export type BindingRepositoryStore = BindingRepositoryState &
  BindingRepositoryActions;

export const useBindingRepositoryStore = create<BindingRepositoryStore>(
  (set, get) => ({
    bindings: new Map<string, DataBindingConfig>(),

    set: (formID: string, config: DataBindingConfig): void => {
      set((state) => {
        const newBindings = new Map(state.bindings);
        newBindings.set(formID, config);
        return { bindings: newBindings };
      });
    },

    get: (formID: string): DataBindingConfig | undefined => {
      return get().bindings.get(formID);
    },

    getAll: (): DataBindingConfig[] => {
      return Array.from(get().bindings.values());
    },

    delete: (formID: string): void => {
      set((state) => {
        const newBindings = new Map(state.bindings);
        newBindings.delete(formID);
        return { bindings: newBindings };
      });
    },

    has: (formID: string): boolean => {
      return get().bindings.has(formID);
    },

    clear: (): void => {
      set({ bindings: new Map() });
    },
  })
);
